import { useState } from 'react';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useAdminWithdrawals, formatCents, useApproveWithdrawal, useRejectWithdrawal } from '@/services/referrals';
import { AdminWithdrawal } from '@/models/referral';
import { darkPanelStyle, darkText } from '@/shared/dark-inline-styles';

const STATUSES = [
    { value: '', label: 'All' },
    { value: 'requested', label: 'Requested' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
];

const WithdrawalsTab = () => {
    const [status, setStatus] = useState('requested');
    const [limit, setLimit] = useState(50);
    const { data, isLoading } = useAdminWithdrawals(status || undefined, limit);
    const approveMutation = useApproveWithdrawal();
    const rejectMutation = useRejectWithdrawal();
    const [pendingAction, setPendingAction] = useState<{ withdrawal: AdminWithdrawal; userId: string; mode: 'approve' | 'reject' } | null>(null);

    if (isLoading) return <LoadingSpinner />;
    if (data?.status === 'error') return <div className="alert alert-danger">{data.error?.message || 'Failed to load withdrawals.'}</div>;
    const withdrawals = data?.data?.withdrawals || [];

    const exportCsv = () => {
        const header = 'id,userId,amountCents,currency,method,status,requestedAt,completedAt,stripeTransferId';
        const rows = withdrawals.map((w) =>
            [w.id, w.userId, w.amountCents, w.currency, w.method, w.status, w.requestedAt, w.completedAt || '', w.stripeTransferId || ''].join(','),
        );
        const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referral-withdrawals-${status || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div>
            <div className="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <select className="form-select" style={{ width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                            {s.label}
                        </option>
                    ))}
                </select>
                <select className="form-select" style={{ width: 120 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                </select>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportCsv}>
                    Export CSV
                </button>
                <span className="text-muted ms-auto">{withdrawals.length} results</span>
            </div>
            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Withdrawal id</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Status</th>
                            <th>Requested</th>
                            <th>Completed</th>
                            <th>Payout details / reference</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {withdrawals.map((w) => (
                            <tr key={w.id}>
                                <td className="font-monospace small">{w.id}</td>
                                <td>{formatCents(w.amountCents, w.currency)}</td>
                                <td>{w.method}</td>
                                <td>
                                    <span className={`badge text-bg-${statusColor(w.status)}`}>{w.status}</span>
                                </td>
                                <td>{new Date(w.requestedAt).toLocaleString()}</td>
                                <td>{w.completedAt ? new Date(w.completedAt).toLocaleString() : '—'}</td>
                                <td className="small" style={{ maxWidth: 280 }}>
                                    {/* TODO(stripe-connect): once Connect resumes, show w.stripeTransferId. */}
                                    {w.status === 'completed' && w.manualReference ? (
                                        <span className="font-monospace">{w.manualReference}</span>
                                    ) : w.payoutDetails ? (
                                        <span className="d-inline-block text-truncate" style={{ maxWidth: 260 }} title={w.payoutDetails}>
                                            {w.payoutDetails}
                                        </span>
                                    ) : (
                                        <span className="text-muted">—</span>
                                    )}
                                </td>
                                <td className="text-end">
                                    {w.status === 'requested' ? (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-success btn-sm me-1"
                                                onClick={() => setPendingAction({ withdrawal: w, userId: w.userId, mode: 'approve' })}
                                            >
                                                Mark as sent
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => setPendingAction({ withdrawal: w, userId: w.userId, mode: 'reject' })}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    ) : (
                                        <span className="text-muted small">—</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {withdrawals.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center text-muted py-4">
                                    No withdrawals match this filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {pendingAction && (
                <ConfirmModal
                    mode={pendingAction.mode}
                    withdrawal={pendingAction.withdrawal}
                    onConfirm={async ({ reason, reference }) => {
                        // callApi resolves { status:'error' } rather than throwing. Throw on
                        // error so the modal surfaces it and does NOT close as success — this
                        // path debits the user's balance, so a silent failure is unacceptable.
                        const resp =
                            pendingAction.mode === 'approve'
                                ? await approveMutation.mutateAsync({
                                      userId: pendingAction.userId,
                                      withdrawalId: pendingAction.withdrawal.id,
                                      reference,
                                      reason,
                                  })
                                : await rejectMutation.mutateAsync({
                                      userId: pendingAction.userId,
                                      withdrawalId: pendingAction.withdrawal.id,
                                      reason,
                                  });
                        if (resp.status === 'error') {
                            throw new Error(resp.error?.message ?? 'Failed to process withdrawal.');
                        }
                        setPendingAction(null);
                    }}
                    onClose={() => setPendingAction(null)}
                    isPending={approveMutation.isPending || rejectMutation.isPending}
                />
            )}
        </div>
    );
};

const ConfirmModal = ({
    mode,
    withdrawal,
    onConfirm,
    onClose,
    isPending,
}: {
    mode: 'approve' | 'reject';
    withdrawal: AdminWithdrawal;
    onConfirm: (data: { reason?: string; reference?: string }) => Promise<void>;
    onClose: () => void;
    isPending: boolean;
}) => {
    const [reason, setReason] = useState('');
    const [reference, setReference] = useState('');
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        setError(null);
        try {
            await onConfirm({ reason: reason.trim() || undefined, reference: reference.trim() || undefined });
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to process withdrawal.');
        }
    };

    // TODO(stripe-connect): once Connect Express is activated, the approve
    // path resumes calling stripe.transfers.create() and `reference` becomes
    // unused (the Stripe transfer id is the reference). Until then, the
    // admin enters the manual transaction id (Wise / bank tx id) here and
    // it's surfaced to the user in the "withdrawal sent" email.
    const isManualMode = true;

    return (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog" onClick={onClose}>
            <div className="modal-dialog modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {mode === 'approve' ? 'Mark as sent' : 'Reject withdrawal'} — {formatCents(withdrawal.amountCents, withdrawal.currency)}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        {mode === 'approve' ? (
                            <>
                                <div className="alert alert-info py-2 small mb-3">
                                    <strong>Manual processing.</strong> Stripe Connect Express is currently disabled — send the payment via Wise /
                                    bank transfer / etc. using the user's payout details below, then enter the transaction reference and confirm.
                                </div>
                                <div className="alert alert-warning small mb-3" role="alert">
                                    <div className="fw-bold mb-1">⚠ Confirm the payment landed before clicking Mark as sent.</div>
                                    <div>
                                        This action <strong>permanently debits</strong> the user's available balance and triggers a "your withdrawal
                                        has been sent" email. There is <strong>no automatic rollback</strong> if the payment later bounces (Wise
                                        rejection, bank reversal, PayPal hold, etc.) — Connect's <code>transfer.reversed</code> webhook is paused with
                                        the rest of Connect.
                                    </div>
                                    <ul className="mb-0 mt-2 ps-3">
                                        <li>
                                            Open your bank / Wise / PayPal dashboard and verify the transaction is in <em>Sent</em> or{' '}
                                            <em>Completed</em> state, NOT <em>Pending</em> or <em>On hold</em>.
                                        </li>
                                        <li>
                                            If the payment fails after you've clicked Mark as sent, you'll need to manually <strong>Reject</strong> a
                                            fresh withdrawal request from the user (which will refund their balance) and contact them to retry.
                                        </li>
                                    </ul>
                                </div>
                                {withdrawal.payoutDetails ? (
                                    <div className="mb-3">
                                        <label
                                            className="form-label small text-muted text-uppercase fw-bold mb-1"
                                            style={{ letterSpacing: '0.06em' }}
                                        >
                                            Payout details (from user)
                                        </label>
                                        {/* NOT `bg-light`: Bootstrap's `light` keeps its light-grey
                                            value in dark mode (only the body/surface variables flip),
                                            so `.bg-light` + our #dddddd body text was 1.29:1 — the
                                            user's bank details, effectively invisible. */}
                                        <pre
                                            className="rounded p-2 mb-0"
                                            style={{
                                                ...darkPanelStyle,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontSize: 13,
                                                color: darkText,
                                            }}
                                        >
                                            {withdrawal.payoutDetails}
                                        </pre>
                                    </div>
                                ) : (
                                    <div className="alert alert-warning py-2 small mb-3">
                                        No payout details captured for this withdrawal — older record. Reach out to the user at their account email to
                                        collect bank info before sending.
                                    </div>
                                )}
                                <label className="form-label">Manual transaction reference {isManualMode ? '(required)' : '(optional)'}</label>
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="e.g. wise-tx-9d4a8e or BANK-2026-04-29-001"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                />
                                <small className="form-text text-muted d-block mb-3">
                                    Shown to the user in the "withdrawal sent" email so they can match it to their bank record.
                                </small>
                            </>
                        ) : (
                            <p className="text-muted">
                                Rejecting will refund the held balance to the user's available balance and notify them via email.
                            </p>
                        )}
                        <label className="form-label">Reason {mode === 'reject' ? '(required)' : '(optional, audit log)'}</label>
                        <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
                        {error && <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={mode === 'approve' ? 'btn btn-success' : 'btn btn-danger'}
                            disabled={
                                isPending ||
                                (mode === 'reject' && reason.trim().length === 0) ||
                                (mode === 'approve' && isManualMode && reference.trim().length === 0)
                            }
                            onClick={handleConfirm}
                        >
                            {isPending ? 'Working…' : mode === 'approve' ? 'Mark as sent' : 'Reject & refund'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const statusColor = (status: string): string => {
    switch (status) {
        case 'completed':
            return 'success';
        case 'processing':
            return 'warning';
        case 'requested':
            return 'info';
        case 'failed':
            return 'danger';
        default:
            return 'secondary';
    }
};

export default WithdrawalsTab;
